import { Component, inject, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Observable, Subject } from 'rxjs';
import { WebcamImage, WebcamModule } from 'ngx-webcam';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';

export interface WebcamCaptureDialogData {
  title: string;
}

@Component({
  selector: 'lfe-webcam-capture-dialog',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButton, MatIcon, WebcamModule],
  templateUrl: './webcam-capture-dialog.component.html',
  styleUrl: './webcam-capture-dialog.component.css',
  encapsulation: ViewEncapsulation.None
})
export class WebcamCaptureDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<WebcamCaptureDialogComponent>);
  readonly data: WebcamCaptureDialogData = inject(MAT_DIALOG_DATA);
  readonly translateService = inject(LiveFormsTranslateLoaderService);

  private readonly webcamTrigger = new Subject<void>();
  private readonly switchCameraTrigger = new Subject<boolean>();

  showWebcam = signal(true);
  capturedImage = signal<WebcamImage | null>(null);
  errorMessage = signal<string | null>(null);
  availableCameras = signal(0);

  constructor() {
    this.enumerateCameras();
  }

  get webcamTriggerObservable(): Observable<void> {
    return this.webcamTrigger.asObservable();
  }

  get switchCameraObservable(): Observable<boolean> {
    return this.switchCameraTrigger.asObservable();
  }

  ngOnDestroy(): void {
    this.webcamTrigger.complete();
    this.switchCameraTrigger.complete();
  }

  private async enumerateCameras(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableCameras.set(devices.filter(d => d.kind === 'videoinput').length);
    } catch {
      this.availableCameras.set(0);
    }
  }

  triggerCapture(): void {
    this.webcamTrigger.next();
  }

  switchCamera(): void {
    this.switchCameraTrigger.next(true);
  }

  handleImage(webcamImage: WebcamImage): void {
    this.capturedImage.set(webcamImage);
    this.showWebcam.set(false);
  }

  handleInitError(error: unknown): void {
    this.errorMessage.set(this.translateService.instant('liveforms-editor.photo-item.webcam.error'));
    console.error('Webcam init error:', error);
  }

  retake(): void {
    this.capturedImage.set(null);
    this.showWebcam.set(true);
    this.errorMessage.set(null);
  }

  confirm(): void {
    this.dialogRef.close(this.capturedImage());
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
